import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errorText, resolveImage } from "@/lib/api";
import { Loader2, Plus, Trash2, Upload, X, Save } from "lucide-react";

const empty = {
  name: "",
  qualification: "",
  specialty: "",
  image: "",
  bio: "",
  highlights: ["", "", ""],
  order: 0,
};

const field =
  "w-full mt-2 border-0 border-b border-slate-300 px-0 py-2.5 bg-transparent focus:outline-none focus:border-teal text-navy text-sm";
const labelClass = "text-[10px] uppercase tracking-[0.2em] text-slate-500";

const DoctorForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    ...empty,
    ...initial,
    highlights: [...(initial?.highlights || []), "", "", ""].slice(0, 3),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setHighlight = (i, value) =>
    setForm((f) => ({ ...f, highlights: f.highlights.map((h, idx) => (idx === i ? value : h)) }));

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/uploads", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image", data.url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.qualification || !form.specialty) {
      toast.error("Name, qualification and specialty are required.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      qualification: form.qualification,
      specialty: form.specialty,
      image: form.image || "",
      bio: form.bio || "",
      highlights: form.highlights.map((h) => h.trim()).filter(Boolean),
      order: Number(form.order) || 0,
    };
    try {
      if (initial?.id) await api.put(`/doctors/${initial.id}`, payload);
      else await api.post("/doctors", payload);
      toast.success(initial?.id ? "Doctor updated." : "Doctor added.");
      onSaved();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-white border border-navy/10 p-6 lg:p-8 grid lg:grid-cols-[220px_1fr] gap-8"
      data-testid="doctor-form"
    >
      <div>
        <div className="h-56 bg-cream border border-navy/10 overflow-hidden flex items-center justify-center">
          {form.image ? (
            <img src={resolveImage(form.image)} alt={form.name} className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400 uppercase tracking-[0.2em]">No photo</span>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="doctor-photo-input" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="doctor-photo-upload-button"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-navy text-navy py-2.5 text-[11px] uppercase tracking-[0.15em] hover:bg-navy hover:text-white transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {form.image ? "Replace Photo" : "Upload Photo"}
        </button>
        {form.image && (
          <button
            type="button"
            onClick={() => set("image", "")}
            data-testid="doctor-photo-remove-button"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.15em] text-navy/60 hover:text-red-600 transition-colors"
          >
            <Trash2 size={13} /> Remove Photo
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className={labelClass}>Name</label>
          <input data-testid="doctor-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="Dr. Full Name" />
        </div>
        <div>
          <label className={labelClass}>Qualification</label>
          <input data-testid="doctor-qualification-input" value={form.qualification} onChange={(e) => set("qualification", e.target.value)} className={field} placeholder="MBBS, MD (Radiology)" />
        </div>
        <div>
          <label className={labelClass}>Specialty</label>
          <input data-testid="doctor-specialty-input" value={form.specialty} onChange={(e) => set("specialty", e.target.value)} className={field} placeholder="Radiology & Imaging" />
        </div>
        <div>
          <label className={labelClass}>Display Order</label>
          <input data-testid="doctor-order-input" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={field} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Short Bio</label>
          <textarea data-testid="doctor-bio-input" rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} className={`${field} resize-none`} placeholder="Shown inside the profile popup" />
        </div>
        {form.highlights.map((h, i) => (
          <div key={i} className={i === 2 ? "sm:col-span-2" : ""}>
            <label className={labelClass}>Highlight {i + 1}</label>
            <input data-testid={`doctor-highlight-${i}-input`} value={h} onChange={(e) => setHighlight(i, e.target.value)} className={field} />
          </div>
        ))}
        <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="doctor-save-button"
            className="btn-sheen inline-flex items-center gap-2 bg-teal text-white px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-navy transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
          <button
            onClick={onCancel}
            data-testid="doctor-cancel-button"
            className="inline-flex items-center gap-2 border border-navy/20 text-navy px-6 py-3 text-[11px] uppercase tracking-[0.15em] hover:border-navy transition-colors"
          >
            <X size={14} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export const DoctorsAdmin = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doctors");
      setDoctors(data);
    } catch (err) {
      toast.error("Could not load doctors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.name}? This removes the card from the website.`)) return;
    try {
      await api.delete(`/doctors/${doc.id}`);
      toast.success("Doctor removed.");
      load();
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-doctors-panel">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-teal text-[10px] uppercase tracking-[0.3em] font-semibold">Specialists</p>
          <h2 className="font-display text-3xl text-navy mt-1">Doctors on your website</h2>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing({})}
            data-testid="admin-add-doctor-button"
            className="btn-sheen inline-flex items-center gap-2 bg-navy text-white px-5 py-3 text-[11px] uppercase tracking-[0.15em] hover:bg-teal transition-colors"
          >
            <Plus size={14} /> Add Doctor
          </button>
        )}
      </div>

      {editing && (
        <div className="mb-10">
          <DoctorForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        </div>
      )}

      {loading ? (
        <Loader2 className="animate-spin text-teal" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {doctors.map((doc, i) => (
            <div key={doc.id} className="bg-white border border-navy/10 overflow-hidden" data-testid={`admin-doctor-card-${i}`}>
              <div className="h-52 bg-cream overflow-hidden flex items-center justify-center">
                {doc.image ? (
                  <img src={resolveImage(doc.image)} alt={doc.name} className="w-full h-full object-contain" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-400 uppercase tracking-[0.2em]">
                    No photo
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl text-navy">{doc.name}</h3>
                <p className="text-teal text-[10px] uppercase tracking-[0.15em] mt-1">{doc.specialty}</p>
                <p className="text-xs text-slate-500 mt-1">{doc.qualification}</p>
                <div className="flex gap-3 mt-5">
                  <button
                    onClick={() => setEditing(doc)}
                    data-testid={`admin-edit-doctor-${i}`}
                    className="flex-1 border border-navy text-navy py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-navy hover:text-white transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(doc)}
                    data-testid={`admin-delete-doctor-${i}`}
                    className="border border-red-200 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-white transition-colors"
                    aria-label={`Delete ${doc.name}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {doctors.length === 0 && (
            <p className="text-sm text-slate-500">No doctors yet. Use "Add Doctor" to create the first profile.</p>
          )}
        </div>
      )}
    </div>
  );
};
